import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { CurrencyCode } from '@shared/contracts/entities';
import { amountAccessibleLabel, formatAmount, formatSignedAmount, statusLabel } from '@/services/formatters';

/**
 * Banking presentation primitives.
 *
 * Rules encoded here so no feature can drift:
 *  - amounts always show their currency and use tabular numerals;
 *  - status is never colour alone — every badge carries text;
 *  - list rows meet the 44px target and expose one accessible name.
 */

export function Amount({
  minorUnits,
  currency,
  signed = false,
  size = 'base',
  direction,
  className = '',
}: {
  minorUnits: number;
  currency: CurrencyCode;
  signed?: boolean;
  size?: 'base' | 'lg' | 'hero';
  direction?: 'in' | 'out';
  className?: string;
}) {
  const sizeClass = size === 'hero' ? ' tm-amount--hero' : size === 'lg' ? ' tm-amount--lg' : '';
  const toneClass = direction === 'in' ? ' tm-amount--in' : direction === 'out' ? ' tm-amount--out' : '';
  const value = signed
    ? formatSignedAmount(direction === 'out' ? -Math.abs(minorUnits) : minorUnits, currency)
    : formatAmount(minorUnits, currency);
  return (
    <span
      className={`tm-amount${sizeClass}${toneClass} ${className}`.trim()}
      data-financial-figure="true"
    >
      <span aria-hidden="true">{value}</span>
      <span className="tm-visually-hidden">
        {amountAccessibleLabel(direction === 'out' ? -Math.abs(minorUnits) : minorUnits, currency)}
      </span>
    </span>
  );
}

export type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

/** Canonical status → tone. One place, so list and detail can never disagree. */
const STATUS_TONE: Readonly<Record<string, BadgeTone>> = {
  // shared
  active: 'success',
  completed: 'success',
  approved: 'success',
  succeeded: 'success',
  delivered: 'success',
  match: 'success',

  pending: 'warning',
  processing: 'warning',
  queued: 'warning',
  scheduled: 'info',
  'awaiting-approval': 'warning',
  'awaiting-sca': 'warning',
  review: 'warning',
  onboarding: 'info',
  'in-review': 'warning',
  requested: 'warning',
  preparing: 'info',
  shipped: 'info',
  frozen: 'warning',
  ordered: 'info',
  restricted: 'warning',
  'close-match': 'warning',
  unavailable: 'neutral',

  failed: 'danger',
  rejected: 'danger',
  reversed: 'danger',
  returned: 'danger',
  blocked: 'danger',
  suspended: 'danger',
  'no-match': 'danger',

  draft: 'neutral',
  cancelled: 'neutral',
  closed: 'neutral',
  expired: 'neutral',
  invited: 'info',
  'not-required': 'neutral',
  'not-requested': 'neutral',
};

export function StatusBadge({
  status,
  label,
  tone,
}: {
  status: string;
  /** Override the display text without changing the canonical status value. */
  label?: string;
  tone?: BadgeTone;
}) {
  const resolved = tone ?? STATUS_TONE[status] ?? 'neutral';
  return (
    <span className={`tm-badge tm-badge--${resolved}`} data-status={status}>
      {label ?? statusLabel(status)}
    </span>
  );
}

const MEDALLION_ICON: Record<'success' | 'warning' | 'danger', ReactNode> = {
  success: (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 12.5 9 17l11-11" />
    </svg>
  ),
  warning: (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v5" />
      <path d="M12 16h.01" />
    </svg>
  ),
  danger: (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
      <path d="M6 6l12 12M18 6 6 18" />
    </svg>
  ),
};

/**
 * Static outcome medallion for a result screen — a payment sent, a transfer
 * completed, a payment that failed. Shape and colour together, never colour
 * alone, and no motion: the package explicitly prohibits payment confetti and
 * any animation used as the only status signal, so this is deliberately a
 * still icon, not an animated success state.
 */
export function ResultMedallion({ tone }: { tone: 'success' | 'warning' | 'danger' }) {
  return (
    <span className={`tm-medallion tm-medallion--${tone}`} aria-hidden="true">
      {MEDALLION_ICON[tone]}
    </span>
  );
}

/**
 * Leading mark for a transaction row: a directional arrow in a tinted circle.
 * Direction is carried by the arrow's shape as well as its colour, so it
 * survives greyscale — the same discipline as StatusBadge's coloured dot.
 */
export function TransactionMark({ direction }: { direction: 'in' | 'out' }) {
  return (
    <span
      className={`tm-txmark tm-txmark--${direction}`}
      aria-hidden="true"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        {direction === 'in' ? (
          <path d="M12 19V5M6 11l6-6 6 6" />
        ) : (
          <path d="M12 5v14M6 13l6 6 6-6" />
        )}
      </svg>
    </span>
  );
}

export function Initials({ name }: { name: string }) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join('');
  return (
    <span className="tm-avatar" aria-hidden="true">
      {initials || '—'}
    </span>
  );
}

const Chevron = () => (
  <svg
    className="tm-row__chevron"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

export type ListRowProps = {
  /** Renders as a link when `to` is set, otherwise as a button or plain row. */
  to?: string;
  onClick?: () => void;
  leading?: ReactNode;
  title: string;
  meta?: ReactNode;
  end?: ReactNode;
  /** Full accessible name; defaults to title + meta text. */
  ariaLabel?: string;
  state?: unknown;
};

export function ListRow({ to, onClick, leading, title, meta, end, ariaLabel, state }: ListRowProps) {
  const inner = (
    <>
      {leading}
      <span className="tm-row__body">
        <span className="tm-row__title">{title}</span>
        {meta ? <span className="tm-row__meta">{meta}</span> : null}
      </span>
      {end ? <span className="tm-row__end">{end}</span> : null}
      {to ?? onClick ? <Chevron /> : null}
    </>
  );

  if (to) {
    return (
      <Link className="tm-row" to={to} aria-label={ariaLabel} state={state}>
        {inner}
      </Link>
    );
  }
  if (onClick) {
    return (
      <button type="button" className="tm-row" onClick={onClick} aria-label={ariaLabel}>
        {inner}
      </button>
    );
  }
  return (
    <div className="tm-row" style={{ cursor: 'default' }}>
      {inner}
    </div>
  );
}

export function SectionHeader({
  title,
  action,
  id,
}: {
  title: string;
  action?: ReactNode;
  id?: string;
}) {
  return (
    <div className="tm-section-head">
      <h2 className="tm-section-title" id={id}>
        {title}
      </h2>
      {action}
    </div>
  );
}

/**
 * Definition list for key/value detail panes. Uses a real <dl> so the
 * relationship survives screen-reader navigation.
 */
export function DetailList({
  items,
}: {
  items: readonly { label: string; value: ReactNode; hint?: string }[];
}) {
  return (
    <dl style={{ margin: 0, display: 'grid', gap: 12 }}>
      {items.map((item) => (
        <div key={item.label} className="tm-between" style={{ alignItems: 'flex-start' }}>
          <dt className="tm-muted" style={{ flex: '0 0 auto', maxWidth: '46%' }}>
            {item.label}
            {item.hint ? <span className="tm-hint" style={{ display: 'block' }}>{item.hint}</span> : null}
          </dt>
          <dd style={{ margin: 0, textAlign: 'end', fontWeight: 560, minWidth: 0, overflowWrap: 'anywhere' }}>
            {item.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}
